/**
 * ApprovalTimelineNode — single row in the sequential approval chain
 * timeline rendered by ApprovalWorkspace (D4). One node per active approval
 * row, displayed in sequence order with a vertical connector line.
 *
 * Three visual states:
 *   - Locked   sequence > current; greyed out, no actions.
 *   - Active   the lowest-sequence PENDING row; only this node is decidable.
 *              Shows "Decide" button if the logged-in user is this approver.
 *   - Decided  APPROVED / REJECTED / CHANGES_REQUESTED / DELEGATED. Badge
 *              + comments + decided-at date.
 *
 * Linear: MYD-203 (D4).
 */

import { type ReactNode } from 'react'
import {
	CheckCircle2,
	XCircle,
	AlertCircle,
	Forward,
	Clock,
	Lock,
	MessageSquare,
} from 'lucide-react'
import { Button } from 'components/ui/button'
import { Badge } from 'components/ui/badge'
import { cn } from 'lib/utils'

interface ApprovalTimelineNodeProps {
	approval: any
	isCurrent: boolean
	isLast: boolean
	currentUserId: string | null
	onDecide: (approval: any) => void
}

interface StateConfig {
	icon: typeof CheckCircle2
	iconBg: string
	iconColor: string
	label: string
	pillClass: string
	pillIcon: ReactNode
	cardClass: string
}

function fmtDate(iso?: string): string {
	if (!iso) return ''
	try {
		return new Date(iso).toLocaleString()
	} catch {
		return String(iso)
	}
}

function getStateConfig(decision: string, isCurrent: boolean): StateConfig {
	if (decision === 'APPROVED') {
		return {
			icon: CheckCircle2,
			iconBg: 'bg-emerald-100',
			iconColor: 'text-emerald-600',
			label: 'Approved',
			pillClass: 'bg-emerald-100 text-emerald-700',
			pillIcon: <CheckCircle2 className="w-3 h-3" />,
			cardClass: 'border-emerald-200',
		}
	}
	if (decision === 'REJECTED') {
		return {
			icon: XCircle,
			iconBg: 'bg-red-100',
			iconColor: 'text-red-600',
			label: 'Rejected',
			pillClass: 'bg-red-100 text-red-700',
			pillIcon: <XCircle className="w-3 h-3" />,
			cardClass: 'border-red-200',
		}
	}
	if (decision === 'CHANGES_REQUESTED') {
		return {
			icon: AlertCircle,
			iconBg: 'bg-amber-100',
			iconColor: 'text-amber-600',
			label: 'Changes requested',
			pillClass: 'bg-amber-100 text-amber-700',
			pillIcon: <AlertCircle className="w-3 h-3" />,
			cardClass: 'border-amber-200',
		}
	}
	if (decision === 'DELEGATED') {
		return {
			icon: Forward,
			iconBg: 'bg-blue-100',
			iconColor: 'text-blue-600',
			label: 'Delegated',
			pillClass: 'bg-blue-100 text-blue-700',
			pillIcon: <Forward className="w-3 h-3" />,
			cardClass: 'border-blue-200',
		}
	}
	// PENDING — split between Active and Locked
	if (isCurrent) {
		return {
			icon: Clock,
			iconBg: 'bg-[#0080fe]/10',
			iconColor: 'text-[#0080fe]',
			label: 'Awaiting decision',
			pillClass: 'bg-[#0080fe]/10 text-[#0080fe]',
			pillIcon: <Clock className="w-3 h-3" />,
			cardClass: 'border-[#0080fe]/40 ring-1 ring-[#0080fe]/20',
		}
	}
	return {
		icon: Lock,
		iconBg: 'bg-gray-100',
		iconColor: 'text-gray-400',
		label: 'Locked',
		pillClass: 'bg-gray-100 text-gray-500',
		pillIcon: <Lock className="w-3 h-3" />,
		cardClass: 'border-gray-200 opacity-70',
	}
}

const ApprovalTimelineNode = ({
	approval,
	isCurrent,
	isLast,
	currentUserId,
	onDecide,
}: ApprovalTimelineNodeProps) => {
	const decision = approval?.decision ?? 'PENDING'
	const cfg = getStateConfig(decision, isCurrent)
	const Icon = cfg.icon
	const approver =
		typeof approval.approver === 'object' && approval.approver
			? approval.approver
			: null
	const approverName = approver?.name || approver?.email || 'Unknown approver'
	const approverTitle = approver?.title

	const delegatedFromName =
		typeof approval.delegatedFrom === 'object' && approval.delegatedFrom
			? approval.delegatedFrom?.name || approval.delegatedFrom?.email
			: null

	const isMe =
		approver?._id != null &&
		currentUserId != null &&
		String(approver._id) === String(currentUserId)
	const canDecide = isCurrent && decision === 'PENDING' && isMe

	return (
		<div className="relative pl-12 pb-6">
			{/* Vertical connector — drawn behind the icon, stops above the next node's icon */}
			{!isLast && (
				<div
					aria-hidden
					className="absolute left-[19px] top-10 bottom-0 w-px bg-gray-200"
				/>
			)}

			{/* Sequence circle / status icon */}
			<div
				className={cn(
					'absolute left-0 top-0 w-10 h-10 rounded-full flex items-center justify-center',
					cfg.iconBg,
				)}
			>
				<Icon className={cn('w-5 h-5', cfg.iconColor)} />
			</div>

			{/* Body card */}
			<div
				className={cn(
					'rounded-md border bg-white px-4 py-3',
					cfg.cardClass,
				)}
			>
				<div className="flex items-start justify-between gap-3 flex-wrap">
					<div className="min-w-0">
						<div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
							Approver #{approval.sequence}
						</div>
						<div className="font-semibold text-sm text-gray-900 truncate">
							{approverName}
							{isMe && decision === 'PENDING' && (
								<span className="ml-1.5 text-xs font-medium text-[#0080fe]">
									(you)
								</span>
							)}
						</div>
						{approverTitle && (
							<div className="text-xs text-gray-500 truncate">{approverTitle}</div>
						)}
						{delegatedFromName && (
							<div className="text-xs text-blue-600 mt-0.5">
								Delegated from {delegatedFromName}
							</div>
						)}
					</div>

					<div className="flex items-center gap-2 shrink-0">
						<Badge className={cn('text-[10px] gap-1', cfg.pillClass)}>
							{cfg.pillIcon}
							{cfg.label}
						</Badge>
						{canDecide && (
							<Button
								variant="primary"
								onClick={() => onDecide(approval)}
								className="gap-1"
							>
								Decide
							</Button>
						)}
					</div>
				</div>

				{/* Comments + decided-at */}
				{approval.decidedAt && (
					<div className="text-xs text-gray-500 mt-2">
						Decided {fmtDate(approval.decidedAt)}
					</div>
				)}
				{approval.comments && (
					<div className="mt-2 flex items-start gap-1.5 text-sm text-gray-700 bg-gray-50 rounded px-2.5 py-1.5">
						<MessageSquare className="w-3.5 h-3.5 mt-0.5 text-gray-400 shrink-0" />
						<span>{approval.comments}</span>
					</div>
				)}
			</div>
		</div>
	)
}

export default ApprovalTimelineNode
